// src/hooks/api/useMembers.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { serverRequest } from '../../util/server_request';
import { notifyUser } from '../../util/util';

// Query Keys
export const membersKeys = {
  all: ['members'],
  lists: () => [...membersKeys.all, 'list'],
  list: (page, size, search) => [...membersKeys.lists(), { page, size, search }],
  detail: (id) => [...membersKeys.all, 'detail', id],
  search: (query) => [...membersKeys.all, 'search', query],
};

// Fetch members with backend pagination and search
export const useMembers = (page = 0, size = 10, searchText = '') => {
  return useQuery({
    queryKey: membersKeys.list(page, size, searchText),
    queryFn: async () => {
      const params = new URLSearchParams({ size, page });
      if (searchText) params.append('search', searchText);
      const result = await serverRequest('GET', `members?${params}`);
      return result.data || { content: [], totalElements: 0, totalPages: 0, number: 0 };
    },
    placeholderData: (prev) => prev,
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

// Fetch all members (for dropdowns, exports, etc.)
export const useAllMembers = () => {
  return useQuery({
    queryKey: [...membersKeys.all, 'all'],
    queryFn: async () => {
      let page = 0;
      const pageSize = 10000;
      let hasMore = true;
      const uniqueMembers = new Map();

      while (hasMore) {
        const result = await serverRequest('GET', `members?size=${pageSize}&page=${page}`);
        const items = result.data?.content || result.data || [];
        
        if (items.length > 0) {
          items.forEach(item => {
            const uniqueKey = `${item.ssnitNumber || item.ssnit_number}_${item.memberNumber || item.member_no || item.fullName || item.FullName || ''}`;
            
            if (!uniqueMembers.has(uniqueKey)) {
              uniqueMembers.set(uniqueKey, {
                label: item.fullName || item.FullName,
                value: item.ssnitNumber || item.ssnit_number,
                originalData: item,
                reactKey: uniqueKey
              });
            }
          });
          
          if (items.length < pageSize) {
            hasMore = false;
          } else {
            page++;
          }
        } else {
          hasMore = false;
        }
      }

      return Array.from(uniqueMembers.values());
    },
    staleTime: Infinity,
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

// Fetch member details
export const useMember = (memberNo, enabled = true) => {
  return useQuery({
    queryKey: membersKeys.detail(memberNo),
    queryFn: async () => {
      const result = await serverRequest('GET', `members/${memberNo}`);
      return result.data;
    },
    enabled: enabled && !!memberNo,
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

// Create new member
export const useCreateMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (memberData) => {
      return await serverRequest('POST', 'members', memberData);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: membersKeys.all });
      notifyUser('success', 'Member created successfully!', 'Success');
    },
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

// Update member
export const useUpdateMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, memberData }) => {
      return await serverRequest('PUT', `members/${id}`, memberData);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: membersKeys.all });
      queryClient.invalidateQueries({ queryKey: membersKeys.detail(variables.id) });
      notifyUser('success', 'Member updated successfully!', 'Success');
    },
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

// Fetch member beneficiaries
export const useMemberBeneficiaries = (memberNumber, ssnitNumber, enabled = false) => {
  return useQuery({
    queryKey: [...membersKeys.all, 'beneficiaries', ssnitNumber],
    queryFn: async () => {
      const result = await serverRequest('GET', `members/beneficiaries?staff-number=&ssnit-number=${ssnitNumber}`);
      return result.data || [];
    },
    enabled: enabled && !!ssnitNumber,
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

// Sync members from external source
export const useSyncMembers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return await serverRequest('POST', 'members/sync');
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: membersKeys.all });
      notifyUser('success', data?.message || 'Members synced successfully!', 'Success');
    },
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

// Delete member
export const useDeleteMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (memberId) => {
      return await serverRequest('DELETE', `members/${memberId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: membersKeys.all });
      notifyUser('success', 'Member deleted successfully!', 'Success');
    },
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};
